import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, SignOut, Sun, Moon, Monitor, ChatCircleDots, MagnifyingGlass } from "@phosphor-icons/react";
import { useLogout, useMe } from "./auth";
import { useGoal } from "./goalApi";
import { useUnread } from "./messagesApi";
import { isInTelegram, useTgBackButton } from "./useTelegramWebApp";
import { fetchUnreadCount, NOTIF_CHANGED_EVENT } from "./notificationsApi";
import { useTheme } from "./useTheme";
import { useTranslation } from "react-i18next";
import { isoDate } from "./dateUtils";
import { EmailVerifyBanner } from "./components/EmailVerifyBanner";
import { BottomNav, type BottomNavTab } from "./components/BottomNav";

// Immersive full-screen routes — rendered without app chrome (header/nav/footer).
const FOCUS_ROUTES = ["/record"];
import { ChatDrawer } from "./components/ChatDrawer";
import { AthleteCommandBar } from "./components/today/AthleteCommandBar";
import { AthleteHeaderAvatar } from "./components/AthleteHeaderAvatar";
import { GrainOverlay } from "./components/canon/GrainOverlay";
import { resolveFocusPhase } from "./lib/focusTab";
import { resolveNavSlots } from "./navConfig";
import { useReducedMotion } from "./hooks/useReducedMotion";
import { useRaceWeekMode } from "./hooks/useRaceWeekMode";
import { silentTrack } from "./lib/errors";
import { useNewVersionCheck } from "./lib/useNewVersionCheck";
import { useRemoteFlagsSync } from "./lib/featureFlagsSync";
import { LanguageProfileSync } from "./i18n/LanguageProfileSync";

function useNotifCount() {
  const [count, setCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const pollOnce = () =>
      silentTrack(async () => {
        const n = await fetchUnreadCount();
        setCount(n);
      }, "notif_unread_count");
    pollOnce();
    timerRef.current = setInterval(pollOnce, 60_000);
    // Inbox actions (read / snooze / mark-all) announce themselves so the
    // badge drops immediately instead of waiting out the 60s poll.
    window.addEventListener(NOTIF_CHANGED_EVENT, pollOnce);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener(NOTIF_CHANGED_EVENT, pollOnce);
    };
  }, []);

  return count;
}

export function Layout({ children }: { children: ReactNode }) {
  const logout = useLogout();
  const nav = useNavigate();
  const location = useLocation();
  const inTg = isInTelegram();
  const { data: unread } = useUnread();
  const { data: me } = useMe();
  const unreadCount = unread?.unread_count ?? 0;
  const notifCount = useNotifCount();
  const hasCoach = me?.has_coach === true;
  const newVersionAvailable = useNewVersionCheck();

  // Канон: чат — side-sheet в шапке — chat side-sheet drawer state. Header ChatCircleDots
  // toggles this instead of navigating to /chat. The route still works
  // as a fallback for direct links + notification deep links.
  const [chatOpen, setChatOpen] = useState(false);
  const openChat = useCallback(() => setChatOpen(true), []);
  const closeChat = useCallback(() => setChatOpen(false), []);

  // IA Canon v2 (IA_CANON_v2_2026-07-04.md раздел 1) — Фокус-слот по фазе сезона:
  // нет цели → Старты · цель активна → План · race week → Гонка.
  const { data: goal } = useGoal();
  const focusPhase = resolveFocusPhase({
    goalType: goal?.goal_type,
    raceDateISO: goal?.race_date,
    goalStatus: goal?.status,
    todayISO: isoDate(new Date()),
  });

  // DS2 — toggle data-race-week on <html> when within 7 days of A-race,
  // so canon-tokens.css `--motion-race-week: 1.4` kicks in across components.
  useRaceWeekMode();

  // v150 Phase 3 — bridge server lab_flags → client featureFlags registry
  // (currently mirrors `experimental_charts`).
  useRemoteFlagsSync();
  const { theme, toggle, isDark } = useTheme();
  const reducedMotion = useReducedMotion();
  const { t } = useTranslation("nav");
  const { t: tc } = useTranslation("common");

  // IA Canon v2 раздел 1 — единая структура для Solo и Coached: 4 якоря + Фокус.
  // Сегодня и Путь — у ВСЕХ ролей (закрыты Solo-Today gap и Coached-Path gap).
  // Канон: чат — side-sheet в шапке — чат у Coached остаётся header side-sheet, не таб.
  //
  // Сама раскладка живёт в navConfig.ts (Ф3, 2026-07-28) — здесь остаётся
  // только перевод ключей в текст: t() зависит от языка, а конфиг обязан
  // проверяться без поднятия i18n.
  const slots = resolveNavSlots({ focusPhase });
  const tabs = slots.map((s) => ({
    to: s.to,
    label: t(s.labelKey, { defaultValue: s.labelFallback ?? s.labelKey }),
    icon: s.icon,
  }));

  // BackButton показываем на внутренних страницах (не на табах нижней навы)
  const TOP_LEVEL = tabs.map((t) => t.to);
  const isTopLevel = TOP_LEVEL.includes(location.pathname);
  const showBack = inTg && !isTopLevel;
  const onBack = useCallback(() => nav(-1), [nav]);
  useTgBackButton(onBack, showBack);

  // Focus routes — immersive full-screen flows (live GPS recording): no header,
  // no bottom-nav, no footer. The screen owns the whole viewport.
  if (FOCUS_ROUTES.includes(location.pathname)) {
    return (
      <div className="min-h-[100dvh] bg-bg-base text-fg-primary">{children}</div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg-base text-fg-primary">
      {/* Язык UI ↔ язык профиля: серверные тексты (бриф/письма/TG) обязаны
          говорить на языке, который пользователь видит. */}
      <LanguageProfileSync />
      {/* W-FIX-B: новый деплой — предложить обновиться (mobile cache fix) */}
      {newVersionAvailable && (
        <button
          type="button"
          data-testid="new-version-banner"
          onClick={() => window.location.reload()}
          className="w-full bg-accent/15 text-accent text-xs font-medium py-1.5 text-center hover:bg-accent/25 transition-colors"
        >
          {tc("new_version_banner", { defaultValue: "Доступна новая версия — нажми, чтобы обновить" })}
        </button>
      )}
      {/* В TG WebApp шапку не показываем — TG рисует свою */}
      {!inTg && (
        <header className="px-4 h-14 border-b border-bg-divider flex items-center justify-between sticky top-0 z-30 bg-bg-base/95 backdrop-blur supports-[backdrop-filter]:bg-bg-base/70">
          <AthleteHeaderAvatar me={me} />
          <div className="flex items-center gap-1">
            {/* Поиск по разделам. До 2026-07-28 экран /search существовал,
                знал все экраны, линзы и базу знаний — и открывался ТОЛЬКО
                по ⌘K, то есть с клавиатуры. На телефоне единственный
                инструмент, масштабируемый на 124 экрана, был недоступен. */}
            <button
              onClick={() => nav("/search")}
              data-testid="header-search-icon"
              className="p-2 rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-raised transition-colors"
              aria-label={tc("search_label", { defaultValue: "Поиск по разделам" })}
              title={tc("search_label", { defaultValue: "Поиск по разделам" })}
            >
              <MagnifyingGlass className="w-4 h-4" />
            </button>
            {hasCoach && (
              <button
                onClick={openChat}
                data-testid="header-chat-icon"
                className="relative p-2 rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-raised transition-colors"
                aria-label={t("chat")}
                title={t("chat")}
              >
                <ChatCircleDots className={`w-4 h-4 ${unreadCount > 0 ? "text-accent" : ""}`} />
                {unreadCount > 0 && (
                  <span
                    className="absolute top-1 right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-rose-500 text-white text-[8px] font-semibold flex items-center justify-center leading-none ring-1 ring-bg-base num-display"
                    aria-label={tc("unread_count", { count: unreadCount })}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={toggle}
              className="p-2 rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-raised transition-colors"
              aria-label={`${tc("theme_toggle")}: ${theme}`}
              title={theme === "auto" ? tc("theme_auto") : theme === "dark" ? tc("theme_dark") : tc("theme_light")}
            >
              {theme === "auto"
                ? <Monitor className="w-4 h-4" />
                : isDark
                ? <Sun className="w-4 h-4" />
                : <Moon className="w-4 h-4" />}
            </button>
            <button
              onClick={() => nav("/inbox")}
              className="relative p-2 rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-raised transition-colors"
              aria-label={tc("notifications_label")}
            >
              <Bell className={`w-4 h-4 ${notifCount > 0 ? "text-accent" : ""}`} strokeWidth={1.75} />
              {notifCount > 0 && (
                <>
                  <span className="absolute inset-0 rounded-md ring-1 ring-accent/30 animate-bell-pulse pointer-events-none" />
                  <span className="absolute top-1 right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-rose-500 text-white text-[8px] font-semibold flex items-center justify-center leading-none ring-1 ring-bg-base">
                    {notifCount > 99 ? "99+" : notifCount}
                  </span>
                </>
              )}
            </button>
            <button
              onClick={() => logout.mutate(undefined, { onSuccess: () => nav("/login") })}
              className="p-2 -mr-2 rounded-md text-fg-muted hover:text-fg-primary hover:bg-bg-raised transition-colors"
              title={tc("logout_label")}
              aria-label={tc("logout_label")}
            >
              <SignOut className="w-4 h-4" />
            </button>
          </div>
        </header>
      )}

      {/* В TG WebApp нет header'а — bell + chat кнопки floating вверху справа */}
      {inTg && hasCoach && (
        <button
          onClick={openChat}
          data-testid="header-chat-icon-tg"
          className="fixed top-2 right-14 z-40 p-2 rounded-full bg-bg-surface/95 backdrop-blur border border-bg-border text-fg-muted hover:text-fg-primary shadow-md"
          aria-label={t("chat")}
        >
          <ChatCircleDots className={`w-4 h-4 ${unreadCount > 0 ? "text-accent" : ""}`} />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 min-w-[14px] h-[14px] px-0.5 rounded-full bg-rose-500 text-white text-[8px] font-semibold flex items-center justify-center leading-none ring-1 ring-bg-base num-display">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      )}
      {/* Поиск в TG: третья плавающая кнопка. Ряд идёт справа налево —
          колокол right-2, чат right-14 (когда есть тренер), поиск дальше.
          Без тренера чата нет, и поиск занимает освободившееся место. */}
      {inTg && (
        <button
          onClick={() => nav("/search")}
          data-testid="header-search-icon-tg"
          className={`fixed top-2 ${hasCoach ? "right-28" : "right-14"} z-40 p-2 rounded-full bg-bg-surface/95 backdrop-blur border border-bg-border text-fg-muted hover:text-fg-primary shadow-md`}
          aria-label={tc("search_label", { defaultValue: "Поиск по разделам" })}
        >
          <MagnifyingGlass className="w-4 h-4" />
        </button>
      )}
      {inTg && (
        <button
          onClick={() => nav("/inbox")}
          className="fixed top-2 right-2 z-40 p-2 rounded-full bg-bg-surface/95 backdrop-blur border border-bg-border text-fg-muted hover:text-fg-primary shadow-md"
          aria-label={tc("notifications_label")}
        >
          <Bell className={`w-4 h-4 ${notifCount > 0 ? "text-accent" : ""}`} strokeWidth={1.75} />
          {notifCount > 0 && (
            <>
              <span className="absolute inset-0 rounded-full ring-1 ring-accent/30 animate-bell-pulse pointer-events-none" />
              <span className="absolute top-0 right-0 min-w-[14px] h-[14px] px-0.5 rounded-full bg-rose-500 text-white text-[8px] font-semibold flex items-center justify-center leading-none ring-1 ring-bg-base num-display">
                {notifCount > 99 ? "99+" : notifCount}
              </span>
            </>
          )}
        </button>
      )}

      <main className="flex-1 pb-24 relative">
        {/* Canon v4 section 7 material grain — pinned to scrolling main viewport. */}
        <GrainOverlay disabled={reducedMotion} />
        <EmailVerifyBanner />
        {children}
        {/* W-REBUILD-6: юр-ссылки в потоке страницы, НЕ fixed-оверлей —
            fixed z-20 висел поверх календаря, шитов и списков на каждом
            экране. Достижимость Impressum сохранена: подвал есть на
            каждой странице после контента. */}
        {!inTg && (
          <div className="flex justify-center gap-3 pt-6 pb-1">
            <a href="/impressum" className="text-[9px] text-fg-muted/40 hover:text-fg-muted transition-colors px-2 py-0.5">
              Impressum
            </a>
            <a href="/privacy" className="text-[9px] text-fg-muted/40 hover:text-fg-muted transition-colors px-2 py-0.5">
              Datenschutz
            </a>
            <a href="/agb" className="text-[9px] text-fg-muted/40 hover:text-fg-muted transition-colors px-2 py-0.5">
              AGB
            </a>
            <a href="/terms" className="text-[9px] text-fg-muted/40 hover:text-fg-muted transition-colors px-2 py-0.5">
              Coaching
            </a>
            <a href="/contact" className="text-[9px] text-fg-muted/40 hover:text-fg-muted transition-colors px-2 py-0.5">
              {tc("support_footer", { defaultValue: "Contact" })}
            </a>
          </div>
        )}
      </main>

      {/* P2.4 T3 — global ⌘K command bar. Listens for Cmd/Ctrl+K
          anywhere outside an input. Renders nothing until opened. */}
      <AthleteCommandBar />


      <BottomNav
        tabs={tabs.map<BottomNavTab>((tab) => ({
          to: tab.to,
          label: tab.label,
          icon: tab.icon,
        }))}
      />

      {/* Канон: чат — side-sheet в шапке — chat side-sheet. Rendered for any coached athlete
          regardless of TG vs web; closed by default. */}
      {hasCoach && <ChatDrawer open={chatOpen} onClose={closeChat} />}
    </div>
  );
}

// Иконки навигации переехали в components/navIcons.tsx вместе со
// слотами (Ф3, 2026-07-28) — конфиг не может ссылаться на компоненты,
// объявленные внутри этого файла.
//
// Аватар: раньше здесь стояла заметка «убран из вкладок, доступен по
// прямому URL». На деле «по прямому URL» означало НИКАК — во всём
// приложении не было ни одной ссылки на /avatar. Вход добавлен в сетку
// разделов Профиля (Ф2), маршрут больше не сирота.
